import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { Truck, Plus, Edit2, Trash2, Palette } from 'lucide-react';
import { api, extractError } from '../lib/api';
import { useConfirm } from '../lib/confirm';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../lib/auth';

const DEVICE_TYPES = ['Kran', 'LKW', 'Bagger', 'Anhänger', 'PKW', 'Werkzeug', 'Sonstiges'];
const DEFAULT_COLORS = ['#f97316', '#f59e0b', '#84cc16', '#10b981', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899'];

/**
 * DevicesPage – Verwaltung aller Geräte (Kran, LKW, etc.)
 * Zeigt Liste mit Standard-Fahrer, KSt und ermöglicht CRUD.
 */
export default function DevicesPage() {
  const { can } = useAuth();
  const canCreate = can('devices', 'create');
  const canEdit = can('devices', 'edit');
  const canDelete = can('devices', 'delete');
  const { confirm } = useConfirm();
  const [devices, setDevices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const load = async () => {
    const [d, e, cc] = await Promise.all([
      api.get('/devices', { params: { include_inactive: true } }),
      api.get('/employees', { params: { include_inactive: false } }),
      api.get('/cost-centers'),
    ]);
    setDevices(d.data);
    setEmployees(e.data);
    setCostCenters(cc.data);
  };
  useEffect(() => { load(); }, []);

  const empById = useMemo(() => Object.fromEntries(employees.map(e => [e.id, e])), [employees]);
  const kstById = useMemo(() => Object.fromEntries(costCenters.map(c => [c.id, c])), [costCenters]);

  const openCreate = () => {
    setEditing({
      name: '', device_type: 'Kran', inventory_number: '',
      default_driver_employee_id: null, cost_center_id: null,
      color: DEFAULT_COLORS[0], notes: '', active: true,
    });
    setOpen(true);
  };
  const openEdit = (d) => { setEditing({ ...d }); setOpen(true); };

  const save = async () => {
    if (!editing.name?.trim()) { toast.error('Bitte Gerätename angeben.'); return; }
    try {
      const payload = { ...editing };
      if (payload.default_driver_employee_id === '__none__') payload.default_driver_employee_id = null;
      if (payload.cost_center_id === '__none__') payload.cost_center_id = null;
      if (editing.id) {
        const { id, created_at, ...rest } = payload;
        await api.patch(`/devices/${id}`, rest);
        toast.success('Gerät aktualisiert.');
      } else {
        await api.post('/devices', payload);
        toast.success('Gerät angelegt.');
      }
      setOpen(false);
      await load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const removeDevice = async (d) => {
    if (!(await confirm({
      title: 'Gerät löschen?',
      description: `"${d.name}" wird gelöscht (oder deaktiviert, falls Einsätze existieren).`,
      variant: 'destructive', confirmLabel: 'Löschen',
    }))) return;
    try {
      const r = await api.delete(`/devices/${d.id}`);
      if (r.data.deactivated) toast.info('Gerät hatte Einsätze – wurde deaktiviert.');
      else toast.success('Gerät gelöscht.');
      await load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const filtered = showInactive ? devices : devices.filter(d => d.active);

  return (
    <div className="p-4 md:p-6 space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <Truck size={18}/>Geräteverwaltung
            </CardTitle>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-xs">
                <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)}/>
                Inaktive anzeigen
              </label>
              <Button onClick={openCreate} size="sm" data-testid="add-device-button" className={canCreate ? '' : 'hidden'}>
                <Plus size={14} className="mr-1"/>Neues Gerät
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">
              Noch keine Geräte angelegt. Klicke oben rechts auf "Neues Gerät".
            </div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Typ</TableHead>
                <TableHead>Inventar-Nr.</TableHead>
                <TableHead>Standard-Fahrer</TableHead>
                <TableHead>Kostenstelle</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map(d => {
                  const driver = d.default_driver_employee_id ? empById[d.default_driver_employee_id] : null;
                  const kst = d.cost_center_id ? kstById[d.cost_center_id] : null;
                  return (
                    <TableRow key={d.id} data-testid={`device-row-${d.name}`}>
                      <TableCell>
                        <span className="inline-block w-3 h-3 rounded-sm" style={{ background: d.color || '#64748b' }}/>
                      </TableCell>
                      <TableCell className="font-semibold">{d.name}</TableCell>
                      <TableCell><Badge variant="outline">{d.device_type}</Badge></TableCell>
                      <TableCell className="font-mono text-xs">{d.inventory_number || '-'}</TableCell>
                      <TableCell>{driver ? `${driver.first_name} ${driver.last_name}` : <span className="text-slate-400 italic">-</span>}</TableCell>
                      <TableCell>{kst ? <Badge variant="outline"><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: kst.color }}/>{kst.short_code}</Badge> : '-'}</TableCell>
                      <TableCell>{d.active ? <Badge className="bg-emerald-100 text-emerald-900 hover:bg-emerald-100 border-emerald-200">Aktiv</Badge> : <Badge variant="outline">Inaktiv</Badge>}</TableCell>
                      <TableCell className="text-right">
                        {canEdit && <Button size="sm" variant="ghost" onClick={() => openEdit(d)} data-testid={`edit-device-${d.name}`}><Edit2 size={14}/></Button>}
                        {canDelete && <Button size="sm" variant="ghost" onClick={() => removeDevice(d)} className="text-rose-600" data-testid={`delete-device-${d.name}`}><Trash2 size={14}/></Button>}
                        {!canEdit && !canDelete && <span className="text-xs text-slate-400">–</span>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {editing && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing.id ? 'Gerät bearbeiten' : 'Neues Gerät'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Name</Label>
                  <Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} data-testid="device-name" placeholder="z.B. Autokran 30t"/>
                </div>
                <div>
                  <Label>Typ</Label>
                  <Select value={editing.device_type} onValueChange={v => setEditing({ ...editing, device_type: v })}>
                    <SelectTrigger data-testid="device-type"><SelectValue/></SelectTrigger>
                    <SelectContent>{DEVICE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Inventar-Nummer (optional)</Label>
                <Input value={editing.inventory_number || ''} onChange={e => setEditing({ ...editing, inventory_number: e.target.value })} placeholder="z.B. K-001"/>
              </div>
              <div>
                <Label>Standard-Fahrer (optional)</Label>
                <Select value={editing.default_driver_employee_id || '__none__'} onValueChange={v => setEditing({ ...editing, default_driver_employee_id: v === '__none__' ? null : v })}>
                  <SelectTrigger data-testid="device-driver"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">– kein fester Fahrer –</SelectItem>
                    {(() => {
                      const deviceEmps = employees.filter(e => e.active && e.assignment_context === 'device');
                      if (deviceEmps.length === 0) {
                        return (
                          <div className="px-2 py-3 text-xs text-slate-500 italic">
                            Kein Geräte-Mitarbeiter verfügbar.<br/>
                            Setze zuerst einen Mitarbeiter auf Zuordnung "Geräte".
                          </div>
                        );
                      }
                      return deviceEmps.map(e => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.first_name} {e.last_name} 🔧
                        </SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Nur Mitarbeiter mit Zuordnung "Geräte" können Standard-Fahrer werden.
                </p>
              </div>
              <div>
                <Label>Kostenstelle (optional)</Label>
                <Select value={editing.cost_center_id || '__none__'} onValueChange={v => setEditing({ ...editing, cost_center_id: v === '__none__' ? null : v })}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">– keine Zuordnung –</SelectItem>
                    {costCenters.filter(c => !c.deleted && c.active).map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.short_code} – {c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="flex items-center gap-1.5"><Palette size={12}/>Farbe</Label>
                <div className="flex gap-1.5 mt-1 flex-wrap">
                  {DEFAULT_COLORS.map(c => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setEditing({ ...editing, color: c })}
                      className={`w-8 h-8 rounded-md border-2 ${editing.color === c ? 'border-slate-900' : 'border-transparent'}`}
                      style={{ background: c }}
                      aria-label={c}
                    />
                  ))}
                  <input type="color" value={editing.color || '#f97316'} onChange={e => setEditing({ ...editing, color: e.target.value })} className="w-8 h-8 rounded-md cursor-pointer"/>
                </div>
              </div>
              <div>
                <Label>Notiz (optional)</Label>
                <Textarea rows={2} value={editing.notes || ''} onChange={e => setEditing({ ...editing, notes: e.target.value })}/>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.active ?? true} onChange={e => setEditing({ ...editing, active: e.target.checked })}/>
                Aktiv
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
              <Button onClick={save} data-testid="save-device-button">Speichern</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
